import { create } from "zustand";
import type { ManifestLayer } from "../types/contract";

/*
 * Press-time harvest preview on Visualize. Session-only — the real machine column
 * replaces this when the catalog says it landed. Keyed by field id.
 */
export interface YieldPreviewEntry {
  layer: ManifestLayer;
  crop: string | null;
  year: string | null;
  /* * first map visit auto-shows the layer; a later uncheck must stay off */
  autoRevealed: boolean;
}

interface YieldPreviewState {
  byField: Record<string, YieldPreviewEntry[]>;
  put: (
    fieldId: string,
    entry: Omit<YieldPreviewEntry, "autoRevealed"> & { autoRevealed?: boolean },
  ) => void;
  markRevealed: (fieldId: string, layerId: string) => void;
  drop: (fieldId: string, layerId: string) => void;
  clearField: (fieldId: string) => void;
}

export const useYieldPreviewStore = create<YieldPreviewState>((set) => ({
  byField: {},
  put: (fieldId, entry) =>
    set((s) => {
      const cur = s.byField[fieldId] ?? [];
      const prev = cur.find((e) => e.layer.id === entry.layer.id);
      const next: YieldPreviewEntry = {
        layer: entry.layer,
        crop: entry.crop,
        year: entry.year,
        autoRevealed: entry.autoRevealed ?? prev?.autoRevealed ?? false,
      };
      return {
        byField: {
          ...s.byField,
          [fieldId]: [...cur.filter((e) => e.layer.id !== next.layer.id), next],
        },
      };
    }),
  markRevealed: (fieldId, layerId) =>
    set((s) => ({
      byField: {
        ...s.byField,
        [fieldId]: (s.byField[fieldId] ?? []).map((e) =>
          e.layer.id === layerId ? { ...e, autoRevealed: true } : e,
        ),
      },
    })),
  drop: (fieldId, layerId) =>
    set((s) => ({
      byField: {
        ...s.byField,
        [fieldId]: (s.byField[fieldId] ?? []).filter(
          (e) => e.layer.id !== layerId,
        ),
      },
    })),
  clearField: (fieldId) =>
    set((s) => ({ byField: { ...s.byField, [fieldId]: [] } })),
}));
